import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Phone, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Reveal } from "./Reveal";
import { ConsentCheckbox } from "@/components/ConsentCheckbox";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { useBotTrap } from "@/components/BotTrap";

export default function ContactSection({ content, onBook }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [captcha, setCaptcha] = useState("");
  const { honeypot, trapHeaders } = useBotTrap();
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill name, email and message"); return; }
    if (!agreed) { toast.error("Please agree to the Terms and Privacy Policy to continue."); return; }
    if (!captcha) { toast.error("Please complete the human-verification check."); return; }
    setSending(true);
    try {
      await api.post("/inquiries", form, { headers: { "cf-turnstile-response": captcha, ...trapHeaders() } });
      toast.success("Message sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setSending(false);
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative overflow-hidden bg-upland-green text-upland-creme py-24 md:py-36">
      <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden="true">
        <img src="https://images.unsplash.com/photo-1729556223848-761942d8b35a?q=85&w=1800&auto=format&fit=crop" alt="" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-upland-green/45" />
        <div className="absolute inset-0 bg-gradient-to-b from-upland-green via-upland-green/30 to-upland-green" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <Reveal className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.3em] text-upland-creme/60 mb-5">{content.contact_overline || "Get in touch"}</p>
          <h2 className="font-serif text-4xl md:text-6xl leading-tight">{content.contact_title || "Let's start a conversation."}</h2>
          <p className="text-upland-creme/70 mt-6 max-w-md leading-relaxed">
            Ready to book? <button onClick={onBook} data-testid="contact-book-link" className="underline underline-offset-4 hover:text-white">Schedule a consultation</button>. Otherwise, drop us a note and we'll respond within one business day.
          </p>
          <div className="mt-10 space-y-4 text-upland-creme/80">
            <a href={`mailto:${content.contact_email}`} data-testid="contact-email-link" className="flex items-center gap-3 hover:text-white transition-colors w-fit"><Mail size={18} /> {content.contact_email}</a>
            {content.contact_phone && <a href={`tel:${content.contact_phone.replace(/[^\d+]/g, "")}`} data-testid="contact-phone-link" className="flex items-center gap-3 hover:text-white transition-colors w-fit"><Phone size={18} /> {content.contact_phone}</a>}
            {content.contact_address && <div className="flex items-center gap-3"><MapPin size={18} /> {content.contact_address}</div>}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7">
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            {honeypot}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input placeholder="Name *" data-testid="contact-name" value={form.name} onChange={upd("name")} className="w-full bg-transparent border border-upland-creme/30 px-4 py-3.5 placeholder-upland-creme/50 focus:outline-none focus:border-upland-creme" />
              <input placeholder="Email *" type="email" data-testid="contact-email" value={form.email} onChange={upd("email")} className="w-full bg-transparent border border-upland-creme/30 px-4 py-3.5 placeholder-upland-creme/50 focus:outline-none focus:border-upland-creme" />
            </div>
            <input placeholder="Company" data-testid="contact-company" value={form.company} onChange={upd("company")} className="w-full bg-transparent border border-upland-creme/30 px-4 py-3.5 placeholder-upland-creme/50 focus:outline-none focus:border-upland-creme" />
            <textarea placeholder="How can we help? *" rows={5} data-testid="contact-message" value={form.message} onChange={upd("message")} className="w-full bg-transparent border border-upland-creme/30 px-4 py-3.5 placeholder-upland-creme/50 focus:outline-none focus:border-upland-creme resize-none" />
            <ConsentCheckbox checked={agreed} onChange={setAgreed} testid="contact-consent" dark />
            <TurnstileWidget onToken={setCaptcha} theme="dark" />
            <button type="submit" disabled={sending || !agreed || !captcha} data-testid="contact-submit-btn"
              className="bg-upland-creme text-upland-green px-10 py-4 hover:bg-white transition-colors disabled:opacity-50 flex items-center gap-2">
              {sending && <Loader2 className="animate-spin" size={16} />} Send Message
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
